import {
  Center,
  Combobox,
  useCombobox,
  Loader,
  PillsInput,
  Pill,
  ScrollArea,
} from '@mantine/core';
import { ChevronDown } from 'react-feather';
import { useClientTerminology } from '@/hooks/useClientTerminology';
import type { Option } from './InputSelect';
import { renderMultiOption } from './MultiOption';
import { handleBackspaceRemoval } from './useMultiSelect';
import styles from './InputSelect.module.css';

type LoadedOption = Option & { value: string };

interface Props {
  placeholder?: string;
  label?: string;
  /**
   * Options loaded so far, rendered verbatim — the server owns filtering and
   * ordering, so there is no client-side narrowing or sorting.
   */
  options: LoadedOption[];
  onChange: (values: (string | null)[]) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Selected options; rendered as pills whether or not they appear in the
   * currently loaded pages.
   */
  value: Option[];
  isDisabled?: boolean;
  maxMenuHeight?: number;
  searchValue: string;
  onSearchChange: (term: string) => void;
  isFetching: boolean;
  /** More pages exist; scrolling to the bottom calls `onLoadMore`. */
  hasMore: boolean;
  /** Page cap reached; shows a "keep typing to narrow" hint instead of loading. */
  atCap: boolean;
  onLoadMore: () => void;
  onDropdownOpen: () => void;
}

/**
 * Multi-select over server-searched options: the search field drives a remote
 * query and scrolling to the bottom of the dropdown loads the next page.
 * The synchronous, locally-filtered counterpart is `InputSelect`.
 */
export const AsyncInputSelect = ({
  options,
  label,
  onChange,
  onBlur,
  value,
  placeholder,
  maxMenuHeight = 185,
  isDisabled = false,
  searchValue,
  onSearchChange,
  isFetching,
  hasMore,
  atCap,
  onLoadMore,
  onDropdownOpen,
}: Props) => {
  const intl = useClientTerminology();
  const combobox = useCombobox({
    onDropdownOpen,
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const isSelected = (val: string | null) => value.some((s) => s.value === val);

  const handleOptionSubmit = (val: string) => {
    const next = isSelected(val)
      ? value.filter((s) => s.value !== val)
      : [
          ...value,
          options.find((o) => o.value === val) ?? { label: val, value: val },
        ];
    onChange(next.map((s) => s.value));
  };

  const removeOption = (val: string | null) => {
    onChange(value.filter((s) => s.value !== val).map((s) => s.value));
  };

  const testLabel = label ?? 'input-select';

  return (
    <div
      className={styles.root}
      data-testid={testLabel}
      data-test-role="dropdown"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          combobox.closeDropdown();
        }
      }}
    >
      {label && <span className={styles.label}>{label}</span>}
      <Combobox
        store={combobox}
        onOptionSubmit={handleOptionSubmit}
        keepMounted={false}
        withinPortal={false}
      >
        <Combobox.DropdownTarget>
          <PillsInput
            size="md"
            onClick={(e) => {
              if (isDisabled) return;
              // Skip clicks on the pill remove × so removing a pill doesn't
              // also toggle the dropdown.
              if ((e.target as HTMLElement).closest('button')) return;
              if (combobox.dropdownOpened) {
                combobox.closeDropdown();
              } else {
                combobox.openDropdown();
              }
            }}
            disabled={isDisabled}
            classNames={{ input: styles.input }}
            rightSection={
              <ChevronDown size={16} color="var(--mantine-color-grayBrand-7)" />
            }
            rightSectionPointerEvents="none"
          >
            <Pill.Group gap="var(--mantine-spacing-xxs)">
              {value.map((opt) => (
                <Pill
                  size="md"
                  key={opt.value}
                  withRemoveButton={!isDisabled}
                  onRemove={() => removeOption(opt.value)}
                  classNames={{
                    root: styles.pill,
                    remove: styles.pillRemove,
                  }}
                >
                  {opt.label}
                </Pill>
              ))}
              <Combobox.EventsTarget>
                <PillsInput.Field
                  role="combobox"
                  className={styles.field}
                  value={searchValue}
                  placeholder={value.length === 0 ? placeholder : ''}
                  onChange={(e) => {
                    onSearchChange(e.currentTarget.value);
                    combobox.openDropdown();
                  }}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    combobox.closeDropdown();
                    onBlur(e);
                  }}
                  onKeyDown={(e) =>
                    handleBackspaceRemoval(e, searchValue, value, removeOption)
                  }
                  disabled={isDisabled}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
        </Combobox.DropdownTarget>

        <Combobox.Dropdown className={styles.dropdown}>
          <ScrollArea.Autosize
            mah={maxMenuHeight}
            onBottomReached={hasMore && !isFetching ? onLoadMore : undefined}
          >
            <Combobox.Options>
              {options.map((opt) => (
                <Combobox.Option
                  value={opt.value}
                  key={opt.value}
                  data-testid={opt.value}
                  className={styles.dropdownOption}
                  selected={isSelected(opt.value)}
                >
                  {renderMultiOption({
                    option: opt,
                    checked: isSelected(opt.value),
                  })}
                </Combobox.Option>
              ))}
              {!isFetching && options.length === 0 && (
                <Combobox.Empty>
                  {intl.formatMessage({ id: 'search.noResults' })}
                </Combobox.Empty>
              )}
              {atCap && (
                <Combobox.Empty>
                  {intl.formatMessage({ id: 'filter.keepTypingToNarrow' })}
                </Combobox.Empty>
              )}
            </Combobox.Options>
            {isFetching && (
              <Center p="xs">
                <Loader size="xs" type="dots" />
              </Center>
            )}
          </ScrollArea.Autosize>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
};
